// material-ui
import { Chip, TextField } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
// views
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { cloneDeep, pickBy } from 'lodash-es';
import React from 'react';

import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';

interface IProps extends IWithStylesProps {
  field: string; // the name of the parent field in state
  list: IMenuList;
  selectedIDs?: [];
  placeholderText: string;
  handleChange: (list: IMenuList, field: string) => void;
  handleAddNewValue?: (newValue: string) => void;
  freeSolo?: boolean;
  disabled?: boolean;
  width?: string;
}

interface IState {
  options: string[];
  values: string[];
  titleToAbbrevTitleMap: { [key: string]: string };
}

export const DropdownChip = withStyles({
  root: {
    background: 'rgba(165, 165, 165, 0.5)',
    color: 'rgba(58,58,58,1)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(165, 165, 165, 0.40)',
    },
    '&:active': {
      background: 'rgba(165, 165, 165, 0.60)',
    },
    '&:disabled': {
      background: '#E2E2E2',
    },
  },
  label: {
    opacity: '1',
    fontSize: 16,
    fontWeight: 500,
  },
})(Chip);

export const AutocompleteDropdown = decorate(
  {
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      chip: {
        fontWeight: 500,
        fontSize: 20,
        color: 'rgba(0, 0, 0, 0.40)',
        '&:hover': {
          color: 'rgba(0, 0, 0, 0.60)',
        },
      },
      input: {
        marginLeft: 5,
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      options: [] as string[],
      values: [] as string[],
      titleToAbbrevTitleMap: {} as { [key: string]: string },
    };

    componentDidMount() {
      this.generateOptionsAndValues(this.props.list);
    }

    componentDidUpdate(prevProps: IProps) {
      if (
        Object.values(prevProps.list).length !== Object.values(this.props.list).length ||
        (!!prevProps.selectedIDs &&
          !!this.props.selectedIDs &&
          prevProps.selectedIDs.length !== this.props.selectedIDs.length)
      ) {
        this.generateOptionsAndValues(this.props.list);
      }
    }

    // converts a menuList into the options / values used by the autocomplete component
    generateOptionsAndValues = (menuList: IMenuList) => {
      // this map uses the optional abbrevTitle prop to display an abbreviated title name
      const titleToAbbrevTitleMap = {} as { [key: string]: string };
      for (const item of Object.values(menuList)) {
        if (item.abbrevTitle) {
          titleToAbbrevTitleMap[item.title] = item.abbrevTitle;
        }
      }

      const options = Object.values(menuList)
        .filter((item: IListItem) => !item.selected)
        .map((item: IListItem) => item.title)
        .sort();
      const values = Object.values(menuList)
        .filter((item: IListItem) => item.selected)
        .map((item: IListItem) => item.title)
        .sort();

      this.setState(
        {
          options,
          values,
          titleToAbbrevTitleMap,
        },
        () => {
          this.props.handleChange(menuList, this.props.field);
        },
      );
    };

    // create an updated list and a map that tracks if a new value is added
    // if a new value is add, create it in the parent
    handleChangeAutocomplete = (
      _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValues: string[],
    ) => {
      const newValuesUsedMap = {} as { [key: string]: boolean };
      newValues = newValues.map((value) => value.trim()).filter((value) => value.length);
      newValues.forEach((value) => (newValuesUsedMap[value] = false));
      const newList = cloneDeep(this.props.list);

      for (const item of Object.values(newList)) {
        item.selected = newValues.includes(item.title);
        if (item.selected) {
          newValuesUsedMap[item.title] = true;
        }
      }

      // check if we need to add a new value to the list
      if (this.props.freeSolo && this.props.handleAddNewValue) {
        const unusedValues = Object.keys(pickBy(newValuesUsedMap, (value) => !value));
        if (unusedValues.length === 1) {
          this.props.handleAddNewValue(unusedValues[0]);
        }
      }

      this.generateOptionsAndValues(newList);
    };

    render() {
      const { freeSolo, classes, placeholderText, disabled, width } = this.props;
      const { options, values } = this.state;
      return (
        <Autocomplete
          multiple
          openOnFocus
          freeSolo={freeSolo}
          options={options}
          value={values}
          defaultValue={[]}
          onChange={this.handleChangeAutocomplete}
          onInputChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            val: string,
          ) => {
            if (val[val.length - 1] === ',') {
              this.handleChangeAutocomplete(event, [
                ...new Set([...values, val.slice(0, val.length - 1)]),
              ]);
            }
          }}
          onBlur={(event: any) => {
            if (event.target.value.length !== 0) {
              this.handleChangeAutocomplete(event, [...new Set([...values, event.target.value])]);
            }
          }}
          disableCloseOnSelect={true}
          disabled={disabled ? disabled : false}
          style={{
            border: '1px solid rgb(200, 200, 200)',
            borderRadius: '4px',
            width: width || '100%',
            minHeight: 40,
          }}
          renderTags={(tagValues: string[], getTagProps) => {
            tagValues = tagValues.map(
              (tag: string) => this.state.titleToAbbrevTitleMap[tag] || tag,
            );
            return tagValues
              .filter((tag: string) => tag.length)
              .map((option: string, index: number) => {
                return (
                  <DropdownChip
                    label={option}
                    {...getTagProps({ index })}
                    deleteIcon={<CloseIcon className={classes.chip} />}
                  />
                );
              });
          }}
          renderInput={(params: RenderInputParams) => {
            // @ts-ignore
            params.InputProps.disableUnderline = true;
            // @ts-ignore
            params.InputProps.classes = { input: classes.input };
            return <TextField {...params} placeholder={values.length ? '' : placeholderText} />;
          }}
        />
      );
    }
  },
);
